import { createSelector } from 'reselect'
import { omitDeep } from '@utils/omitDeep'
import { FiltersEnum } from '@utils/urlFilters'
import { RootState } from 'common/types/Global'
import { MentionsFilters } from '@views/Config/View/View.types'
import {
  flow,
  mapKeys,
  includes,
  mapValues,
  isArray,
  flatten,
  isBoolean,
  cond,
  identity,
  T,
} from 'lodash/fp'
import { discardNonPrimitiveEmptyValues } from '@utils/JSUtils'
import type { ViewFiltersInput } from 'apollo/generated/globalTypes'

//----------------------------------

/** Clean view filters object, skipped empty values */
export const cleanViewFilters = (
  filters: ViewFiltersInput,
): ViewFiltersInput => {
  const viewFiltersClean: any = omitDeep(filters, '__typename')

  const value = flow(
    mapValues((val: Partial<ViewFiltersInput>) => {
      if (Array.isArray(val)) {
        return val
      }

      return flow(
        mapValues(prop => {
          // if it's an array it means it's a filter value
          if (Array.isArray(prop)) {
            return prop
          }

          // otherwise is an object to we keep going cleaning filter of empty values
          return flow(discardNonPrimitiveEmptyValues)(prop)
        }),
        discardNonPrimitiveEmptyValues,
      )(val)
    }),
    discardNonPrimitiveEmptyValues,
  )(viewFiltersClean)

  return value
}

export const viewFilters = (state: Partial<RootState>): ViewFiltersInput =>
  cleanViewFilters(state.appConfiguration.view?.filters)

// remove plurals
const trxSpecialFilters = (words: string[]) => {
  return mapKeys((word: string) => {
    const customTransform = {
      categories: 'category',
      storesMetadata: FiltersEnum.STORE_METADATA,
    }

    return includes(word, words)
      ? word
      : customTransform[word] || word.slice(0, -1)
  })
}

const toSingular = (words: string[]) => data =>
  mapKeys((word: string) => (includes(word, words) ? word.slice(0, -1) : word))(
    data,
  )
const BLACK_LIST = ['isDark', 'followers']
const WHITE_LIST = ['ands', 'ors', 'nots']

export const parseFiltersToMagicSearch = (
  filters: ViewFiltersInput,
): MentionsFilters => {
  const result = flow(
    trxSpecialFilters(BLACK_LIST),
    mapValues((val: Partial<MentionsFilters>) => {
      if (Array.isArray(val)) {
        return val
      }

      return flow(
        cond([
          [isBoolean, identity],
          [T, val => flow(toSingular(WHITE_LIST))(val)],
        ]),
        cond([
          [isBoolean, identity],
          [T, mapValues(elto => (isArray(elto) ? flatten(elto) : elto))],
        ]),
      )(val)
    }),
  )(filters)

  return result
}

/**
 * Gets filters in current view and format to Magic Search
 * @returns filters in MentionsFilters format
 */
export const parsedAsMagicSearchSelector = createSelector(
  viewFilters,
  filters => parseFiltersToMagicSearch(filters),
)
